/**
 * Course Progression Utility
 * Handles automatic course progression for annual student updates
 */

// Base course names without sections
export const COURSE_BASE_NAMES = [
  'Medio Mayor',
  'Pre Kinder',
  'Kinder',
  'Primero básico',
  'Segundo básico',
  'Tercero básico',
  'Cuarto básico',
  'Quinto básico',
  'Sexto básico',
  'Séptimo básico',
  'Octavo básico',
  'Primero medio',
  'Segundo medio',
  'Tercero medio',
  'Cuarto medio'
] as const;

// Common section letters
export const SECTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

/**
 * Normalizes a course name to match the standard format
 * @param courseName - The course name to normalize
 * @returns Normalized course base name or null if not recognized
 */
export const normalizeCourseBaseName = (courseName: string): string | null => {
  const normalized = courseName.toLowerCase().trim();
  
  // Mapping of variations to standard names
  const mappings: { [key: string]: string } = {
    'medio mayor': 'Medio Mayor',
    'nivel medio mayor': 'Medio Mayor',
    'pre kinder': 'Pre Kinder',
    'prekinder': 'Pre Kinder',
    'prekínder': 'Pre Kinder',
    'pre-kinder': 'Pre Kinder',
    'kinder': 'Kinder',
    'kínder': 'Kinder',
    'primero básico': 'Primero básico',
    'primero basico': 'Primero básico',
    '1° básico': 'Primero básico',
    '1° basico': 'Primero básico',
    '1º básico': 'Primero básico',
    '1º basico': 'Primero básico',
    'segundo básico': 'Segundo básico',
    'segundo basico': 'Segundo básico',
    '2° básico': 'Segundo básico',
    '2° basico': 'Segundo básico',
    '2º básico': 'Segundo básico',
    '2º basico': 'Segundo básico',
    'tercero básico': 'Tercero básico',
    'tercero basico': 'Tercero básico',
    '3° básico': 'Tercero básico',
    '3° basico': 'Tercero básico',
    '3º básico': 'Tercero básico',
    '3º basico': 'Tercero básico',
    'cuarto básico': 'Cuarto básico',
    'cuarto basico': 'Cuarto básico',
    '4° básico': 'Cuarto básico',
    '4° basico': 'Cuarto básico',
    '4º básico': 'Cuarto básico',
    '4º basico': 'Cuarto básico',
    'quinto básico': 'Quinto básico',
    'quinto basico': 'Quinto básico',
    '5° básico': 'Quinto básico',
    '5° basico': 'Quinto básico',
    '5º básico': 'Quinto básico',
    '5º basico': 'Quinto básico',
    'sexto básico': 'Sexto básico',
    'sexto basico': 'Sexto básico',
    '6° básico': 'Sexto básico',
    '6° basico': 'Sexto básico',
    '6º básico': 'Sexto básico',
    '6º basico': 'Sexto básico',
    'séptimo básico': 'Séptimo básico',
    'septimo básico': 'Séptimo básico',
    'septimo basico': 'Séptimo básico',
    '7° básico': 'Séptimo básico',
    '7° basico': 'Séptimo básico',
    '7º básico': 'Séptimo básico',
    '7º basico': 'Séptimo básico',
    'octavo básico': 'Octavo básico',
    'octavo basico': 'Octavo básico',
    '8° básico': 'Octavo básico',
    '8° basico': 'Octavo básico',
    '8º básico': 'Octavo básico',
    '8º basico': 'Octavo básico',
    'primero medio': 'Primero medio',
    '1° medio': 'Primero medio',
    '1º medio': 'Primero medio',
    'segundo medio': 'Segundo medio',
    '2° medio': 'Segundo medio',
    '2º medio': 'Segundo medio',
    'tercero medio': 'Tercero medio',
    '3° medio': 'Tercero medio',
    '3º medio': 'Tercero medio',
    'cuarto medio': 'Cuarto medio',
    '4° medio': 'Cuarto medio',
    '4º medio': 'Cuarto medio',
  };

  // Try direct mapping first
  for (const [pattern, standardName] of Object.entries(mappings)) {
    if (normalized.includes(pattern)) {
      return standardName;
    }
  }

  return null;
};

/**
 * Extracts the section letter from a course name
 * @param courseName - Full course name (e.g., "Primero básico A")
 * @returns Section letter (e.g., "A") or null if not found
 */
export const extractSection = (courseName: string): string | null => {
  const trimmed = courseName.trim();
  const lastChar = trimmed.charAt(trimmed.length - 1).toUpperCase();
  
  if (SECTION_LETTERS.includes(lastChar as typeof SECTION_LETTERS[number])) {
    return lastChar;
  }
  
  // Try to find section in format "Curso X" where X is a letter
  const match = trimmed.match(/\s+([A-Fa-f])$/);
  return match ? match[1].toUpperCase() : null;
};

/**
 * Gets the next course in the progression
 * @param currentCourse - The current course name (e.g., "Primero básico A")
 * @returns The next course name with the same section, or null if at the end
 */
export const getNextCourse = (currentCourse: string): string | null => {
  const baseName = normalizeCourseBaseName(currentCourse);
  const section = extractSection(currentCourse);
  
  if (!baseName) {
    console.warn(`Could not normalize course name: ${currentCourse}`);
    return null;
  }
  
  const currentIndex = COURSE_BASE_NAMES.indexOf(baseName as typeof COURSE_BASE_NAMES[number]);
  
  if (currentIndex === -1) {
    console.warn(`Course not found in progression: ${baseName}`);
    return null;
  }
  
  // Check if this is the last course
  if (currentIndex >= COURSE_BASE_NAMES.length - 1) {
    return null; // Graduated/Egresado
  }
  
  const nextBaseName = COURSE_BASE_NAMES[currentIndex + 1];
  
  // Maintain the same section if it exists
  if (section) {
    return `${nextBaseName} ${section}`;
  }
  
  return nextBaseName;
};

/**
 * Gets the previous course in the progression
 * @param currentCourse - The current course name (e.g., "Primero básico A")
 * @returns The previous course name with the same section, or null if at the start
 */
export const getPreviousCourse = (currentCourse: string): string | null => {
  const baseName = normalizeCourseBaseName(currentCourse);
  const section = extractSection(currentCourse);
  
  if (!baseName) {
    return null;
  }
  
  const currentIndex = COURSE_BASE_NAMES.indexOf(baseName as typeof COURSE_BASE_NAMES[number]);
  
  if (currentIndex <= 0) {
    return null; // Already at the first course
  }
  
  const prevBaseName = COURSE_BASE_NAMES[currentIndex - 1];
  
  if (section) {
    return `${prevBaseName} ${section}`;
  }
  
  return prevBaseName;
};

/**
 * Gets the course index (0-based) for ordering purposes
 * @param courseName - The course name
 * @returns The index (0-14) or -1 if not found
 */
export const getCourseIndex = (courseName: string): number => {
  const baseName = normalizeCourseBaseName(courseName);
  if (!baseName) return -1;
  
  return COURSE_BASE_NAMES.indexOf(baseName as typeof COURSE_BASE_NAMES[number]);
};

/**
 * Checks if a student is graduating (finishing the last course)
 * @param currentCourse - The student's current course
 * @returns true if student will graduate next year
 */
export const isGraduating = (currentCourse: string): boolean => {
  const baseName = normalizeCourseBaseName(currentCourse);
  if (!baseName) return false;
  
  const currentIndex = COURSE_BASE_NAMES.indexOf(baseName as typeof COURSE_BASE_NAMES[number]);
  return currentIndex === COURSE_BASE_NAMES.length - 1;
};

/**
 * Parses a full student name into components
 * Assumes format: "APELLIDO PATERNO APELLIDO MATERNO NOMBRES" or "NOMBRES APELLIDO PATERNO APELLIDO MATERNO"
 * @param fullName - The full name string
 * @returns Object with firstName, paternalLastName, maternalLastName
 */
export const parseStudentName = (fullName: string): {
  firstName: string;
  paternalLastName: string;
  maternalLastName: string;
} => {
  const parts = fullName.trim().split(/\s+/);
  
  if (parts.length === 0) {
    return { firstName: '', paternalLastName: '', maternalLastName: '' };
  }
  
  if (parts.length === 1) {
    return { firstName: parts[0], paternalLastName: '', maternalLastName: '' };
  }
  
  if (parts.length === 2) {
    return { firstName: parts[0], paternalLastName: parts[1], maternalLastName: '' };
  }
  
  // For 3+ parts, check if format is "LASTNAME LASTNAME FIRSTNAME(S)"
  // Common in Chilean school systems where names are stored as "APELLIDO PATERNO APELLIDO MATERNO NOMBRES"
  // We detect this by checking if the last part(s) look like first names (often mixed case or common names)
  
  // Simple heuristic: if all parts are uppercase, assume it's APELLIDO APELLIDO NOMBRE(S)
  const allUppercase = parts.every(part => part === part.toUpperCase());
  
  if (allUppercase && parts.length >= 3) {
    // Format: APELLIDO_PATERNO APELLIDO_MATERNO NOMBRE(S)
    return {
      firstName: parts.slice(2).join(' '),
      paternalLastName: parts[0],
      maternalLastName: parts[1]
    };
  }
  
  // Otherwise assume NOMBRE APELLIDO_PATERNO APELLIDO_MATERNO
  if (parts.length === 3) {
    return {
      firstName: parts[0],
      paternalLastName: parts[1],
      maternalLastName: parts[2]
    };
  }
  
  // For more than 3 parts, assume first half are names, last two are surnames
  const lastNameStart = Math.max(parts.length - 2, 1);
  return {
    firstName: parts.slice(0, lastNameStart).join(' '),
    paternalLastName: parts[lastNameStart] || '',
    maternalLastName: parts[lastNameStart + 1] || ''
  };
};

/**
 * Combines name components into a full name
 * @param firstName - First name(s)
 * @param paternalLastName - Paternal last name
 * @param maternalLastName - Maternal last name
 * @returns Combined full name in format "APELLIDO_PATERNO APELLIDO_MATERNO NOMBRE(S)"
 */
export const combineStudentName = (
  firstName: string,
  paternalLastName: string,
  maternalLastName: string
): string => {
  const parts = [paternalLastName, maternalLastName, firstName]
    .filter(part => part && part.trim())
    .map(part => part.trim().toUpperCase());
  
  return parts.join(' ');
};

/**
 * Formats a date string to YYYY-MM-DD format
 * @param dateString - Date in various formats
 * @returns Date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForExcel = (dateString: string): string => {
  if (!dateString) return '';
  
  // Try to parse the date
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    // Try DD/MM/YYYY format
    const parts = dateString.split(/[\/\-\.]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const parsedDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    }
    return dateString;
  }
  
  return date.toISOString().split('T')[0];
};

/**
 * Parses a date from Excel (which might be a number or string)
 * @param excelDate - Date value from Excel
 * @returns Date string in YYYY-MM-DD format
 */
export const parseDateFromExcel = (excelDate: string | number): string => {
  if (!excelDate) return '';
  
  // Excel stores dates as numbers (days since 1900-01-01)
  if (typeof excelDate === 'number') {
    const date = new Date((excelDate - 25569) * 86400 * 1000);
    return date.toISOString().split('T')[0];
  }
  
  // If it's already a string, try to parse it
  return formatDateForExcel(String(excelDate));
};

/**
 * Validates a RUT (Chilean ID number)
 * @param rut - RUT string with or without formatting
 * @returns true if valid, false otherwise
 */
export const validateRut = (rut: string): boolean => {
  if (!rut) return false;
  
  // Remove dots and dashes
  const cleanRut = rut.replace(/\./g, '').replace(/-/g, '').trim();
  
  if (cleanRut.length < 2) return false;
  
  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1).toUpperCase();
  
  // Validate that body is numeric
  if (!/^\d+$/.test(body)) return false;
  
  // Calculate verification digit
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = 11 - (sum % 11);
  const calculatedDV = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
  
  return dv === calculatedDV;
};

/**
 * Formats a RUT for display (XX.XXX.XXX-X)
 * @param rut - RUT string
 * @returns Formatted RUT or original if invalid
 */
export const formatRut = (rut: string): string => {
  if (!rut) return '';
  
  // Remove all non-alphanumeric characters
  const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (cleanRut.length < 2) return rut;
  
  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1);
  
  // Add dots every 3 digits from right
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${formattedBody}-${dv}`;
};

/**
 * Cleans a RUT for database storage (just numbers and K)
 * @param rut - RUT in any format
 * @returns Clean RUT (numbers only + DV)
 */
export const cleanRut = (rut: string): string => {
  if (!rut) return '';
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
};

export default {
  COURSE_BASE_NAMES,
  SECTION_LETTERS,
  normalizeCourseBaseName,
  extractSection,
  getNextCourse,
  getPreviousCourse,
  getCourseIndex,
  isGraduating,
  parseStudentName,
  combineStudentName,
  formatDateForExcel,
  parseDateFromExcel,
  validateRut,
  formatRut,
  cleanRut
};

